import { useCustomMutation, useCustomQuery } from '../../graphql';

// Mirrors apps/gripp-web/src/issues/hooks/useShareIssue.ts's shape (mint +
// resolve as separate thin hooks in one file) rather than sharing a module
// with it. The two diverge in more than the subject-type literal: this file
// also owns redeem, which the ISSUE path doesn't have; `subjectId` here is
// always a workspaceasset id, never an asset id; and the mint gate an ASSET
// link sits behind (account.invite) differs from the ISSUE gate. Extracting
// a shared module today would mean either forking it again the moment one
// side needs something the other doesn't, or coupling the two subject
// types' evolution for the sake of deduplicating a few lines of GraphQL.

const CREATE_ASSET_SHARE_LINK = `
  mutation CreateAssetShareLink($subjectId: ID!) {
    share {
      createShareLink(subjectType: ASSET, subjectId: $subjectId) {
        token
        expiresAt
      }
    }
  }
`;

// subjectId is the workspaceasset id (useWorkspaceAsset's row id), never the
// asset id — the membership write on redeem needs an unambiguous workspace,
// and a bare asset has one row per workspace. createShareLink is idempotent,
// so the panel may call this on every open.
export const useCreateAssetShareLink = () =>
  useCustomMutation({ query: CREATE_ASSET_SHARE_LINK });

const RESOLVE_ASSET_SHARE_LINK = `
  query ResolveAssetShareLink($token: String!) {
    share {
      resolveShareLink(token: $token) {
        subjectType
        subjectId
        workspaceId
        assetId
        assetName
        workspaceName
        sharedBy {
          id
          name {
            first
            last
          }
        }
      }
    }
  }
`;

// Public (no auth required) — the Join landing reads this before sign-in.
// assetName/workspaceName/workspaceId/assetId are populated for ASSET links
// only and come back null for any other subject type, or once the subject is
// gone. sharedBy is the loadable Account: only id/name resolve under a Pothos
// root field, so no other Account field is selected here (see memory
// 0035-pothos-root-fields-publish-only-the-pothos).
//
// Keyed apart from useShareIssue's `['share', 'resolveShareLink', token]`
// deliberately. The two select DIFFERENT fields off the same query, and
// `/share/:token` mounts both against the same token — the ISSUE resolve to
// pick a branch, then this one for the Join labels. One shared key means one
// react-query entry whose queryFn belongs to whichever observer mounted last,
// so the labels arrive absent on first paint and the two observers refetch
// over each other.
export const useResolveAssetShareLink = (token?: string) =>
  useCustomQuery({
    query: RESOLVE_ASSET_SHARE_LINK,
    variables: { token },
    cacheKey: ['share', 'resolveAssetShareLink', token],
    options: { enabled: !!token },
  });

const REDEEM_ASSET_SHARE_LINK = `
  mutation RedeemAssetShareLink($token: String!) {
    share {
      redeemShareLink(token: $token) {
        joined
        workspaceId
        workspaceAssetId
        assetId
      }
    }
  }
`;

// Writes membership only — it does NOT switch the caller's active workspace.
// The Join sequence (task 3.8) is: redeem, then useChangeWorkspace(result.
// workspaceId), then refetch currentAccount, then navigate to
// result.assetId/workspaceAssetId. joined:false means the caller was already
// a member and nothing was written; the client still lands them on the asset.
export const useRedeemAssetShareLink = () =>
  useCustomMutation({ query: REDEEM_ASSET_SHARE_LINK });
